/**
 * Fee guide — Restore.Dental
 * Prices are indicative "from" / range figures; final quote after examination.
 * Keep in sync with services.js FAQ answers.
 */

export const PRICING_LAST_UPDATED = '2026-06-01';

export const pricingCategories = [
  {
    id: 'consultations',
    title: 'Consultations & Check-ups',
    items: [
      { treatment: 'New patient consultation (exam, photos, digital X-rays)', price: 'Free*', note: '*Free when you proceed with a treatment plan', link: '/dentist-acton' },
      { treatment: 'Routine dental examination', price: 'from £80', link: '/dentist-acton' },
      { treatment: 'Emergency examination & X-rays', price: '£80 – £120', link: '/emergency-dentist-acton' },
      { treatment: 'Digital Smile Design consultation', price: '£200 – £300', note: 'Credited against treatment if you proceed', link: '/services/digital-smile-design' },
    ],
  },
  {
    id: 'invisalign',
    title: 'Invisalign & Teeth Straightening',
    items: [
      { treatment: 'Invisalign Lite (simple cases, up to 14 aligners)', price: 'from £2,800', link: '/invisalign-acton' },
      { treatment: 'Invisalign Comprehensive (moderate–complex cases)', price: '£3,500 – £5,500', link: '/invisalign-acton' },
      { treatment: 'Complex Invisalign (extended treatment)', price: 'up to £6,000', link: '/services/invisalign' },
      { treatment: 'Retainers (post-treatment, per set)', price: 'included', note: 'Vivera retainers included with Invisalign treatment', link: '/invisalign-acton' },
    ],
  },
  {
    id: 'implants',
    title: 'Dental Implants',
    items: [
      { treatment: 'Single dental implant (implant, abutment & crown)', price: 'from £2,295', note: '3D CBCT planning included', link: '/implants-acton' },
      { treatment: 'Implant-retained bridge (3 teeth on 2 implants)', price: 'from £5,500', link: '/services/dental-implants' },
      { treatment: 'All-on-4 full-arch implants (per arch)', price: '£12,000 – £18,000', link: '/services/all-on-4-implants' },
      { treatment: 'All-on-4 both arches', price: '£24,000 – £35,000', link: '/services/all-on-4-implants' },
      { treatment: 'Bone grafting (where required)', price: 'from £450', link: '/services/dental-implants' },
    ],
  },
  {
    id: 'cosmetic',
    title: 'Cosmetic Dentistry',
    items: [
      { treatment: 'Composite bonding (per tooth)', price: 'from £250', link: '/composite-bonding-acton' },
      { treatment: 'Composite veneers (per tooth)', price: '£400 – £600', link: '/services/composite-veneers' },
      { treatment: 'Porcelain veneers (per tooth)', price: '£800 – £1,200', link: '/services/porcelain-veneers' },
      { treatment: 'Full smile makeover — composite (8–10 teeth)', price: '£3,500 – £6,000', link: '/cosmetic-dentist-acton' },
      { treatment: 'Full smile makeover — porcelain (8–10 veneers)', price: '£7,000 – £12,000', link: '/cosmetic-dentist-acton' },
    ],
  },
  {
    id: 'whitening',
    title: 'Teeth Whitening',
    items: [
      { treatment: 'Home whitening (custom trays & professional gel)', price: '£350 – £450', link: '/services/teeth-whitening' },
      { treatment: 'In-chair whitening (single visit)', price: '£500 – £650', link: '/services/teeth-whitening' },
      { treatment: 'Combined in-chair + home whitening', price: '£700 – £850', link: '/services/teeth-whitening' },
    ],
  },
  {
    id: 'restorative',
    title: 'Restorative & General Dentistry',
    items: [
      { treatment: 'White (composite) filling', price: 'from £150', link: '/dentist-acton' },
      { treatment: 'Dental crown (ceramic / zirconia)', price: 'from £850', link: '/services/crowns' },
      { treatment: 'Dental bridge (per unit)', price: 'from £850', link: '/services/bridges' },
      { treatment: 'Root canal treatment', price: 'from £450', link: '/dentist-acton' },
      { treatment: 'Tooth extraction', price: '£150 – £300', link: '/emergency-dentist-acton' },
      { treatment: 'Dentures (per arch)', price: 'from £950', link: '/services/dentures' },
      { treatment: 'Hygienist visit (scale & polish)', price: 'from £85', link: '/dentist-acton' },
    ],
  },
  {
    id: 'tmj',
    title: 'Jaw Pain, Grinding & Facial Aesthetics',
    items: [
      { treatment: 'Custom bite splint / night guard', price: '£350 – £500', link: '/services/splints' },
      { treatment: 'Bite adjustment', price: '£100 – £200', link: '/services/tmj-dentistry' },
      { treatment: 'Botox for bruxism / jaw clenching (masseter)', price: 'from £350', link: '/services/botox' },
      { treatment: 'Sedation (IV, per session)', price: 'from £350', link: '/services/sedation' },
    ],
  },
  {
    id: 'reconstruction',
    title: 'Full Mouth Reconstruction',
    items: [
      { treatment: 'Full mouth reconstruction (multi-disciplinary)', price: '£15,000 – £40,000+', note: 'Staged over 12–18 months; itemised written plan provided', link: '/services/full-mouth-reconstruction' },
    ],
  },
];

export const financeSummary = {
  headline: '0% interest-free finance from 3 to 60 months',
  bullets: [
    'Spread treatments between £500 and £30,000',
    'Instant decision — 2-minute online application via Tabeo',
    'Invisalign from £83/month · Single implant from £191/month (0% over 12 months)',
    'No deposit options available, subject to status',
  ],
};

export const pricingFAQs = [
  {
    question: 'Are your dental prices fixed or do they change after the consultation?',
    answer: 'The figures on this page are our standard fee guide. After your examination you receive a written, itemised treatment plan — the price you are quoted is the price you pay. There are no hidden fees for X-rays, planning or follow-up appointments within your plan.',
  },
  {
    question: 'Do you offer 0% finance on dental treatment?',
    answer: 'Yes. We offer 0% interest-free finance over 3–60 months on treatment between £500 and £30,000 through our partner Tabeo. The 2-minute application gives an instant decision and you can start treatment straight away.',
  },
  {
    question: 'Is the first consultation really free?',
    answer: 'Your new-patient consultation — including a clinical examination, photographs and digital X-rays — is free when you go ahead with a treatment plan. If you only want a standalone exam and report, the fee is from £80.',
  },
  {
    question: 'How much does Invisalign cost in Acton, West London?',
    answer: 'At Restore.Dental Invisalign starts from £2,800 for Invisalign Lite (simple cases), with comprehensive treatment typically costing £3,500–£5,500. Retainers are included. With 0% finance that works out from around £83 per month.',
  },
  {
    question: 'How much is a single dental implant?',
    answer: 'A single dental implant including the abutment and porcelain crown starts from £2,295, with 3D CBCT planning included. Bone grafting, if needed, is quoted separately from £450.',
  },
  {
    question: 'Do you accept NHS patients?',
    answer: 'Restore.Dental is a private practice, which allows us to spend more time with each patient and use premium materials and technology. We keep our fees transparent and offer 0% finance so treatment remains accessible.',
  },
];
